import LegalLayout, {
  LegalSection,
  LegalList,
} from "@/components/common/LegalLayout";

/**
 * Public Privacy Policy page for the PNBP Document Management System (PNBP DMS),
 * an internal knowledge-management platform of Direktorat Jenderal Anggaran (DJA),
 * Kementerian Keuangan Republik Indonesia.
 */
export default function PrivacyPolicyPage() {
  return (
    <LegalLayout
      title="Kebijakan Privasi"
      subtitle="Kebijakan ini menjelaskan bagaimana Arsitektur Informasi PNBP mengumpulkan, menggunakan, menyimpan, dan melindungi data dalam pengelolaan dokumen Penerimaan Negara Bukan Pajak (PNBP)."
      lastUpdated="11 Juni 2026"
    >
      <LegalSection title="1. Pendahuluan">
        <p>
          PNBP Document Management System (selanjutnya disebut "Platform")
          adalah sistem manajemen dokumen dan pengetahuan yang dikelola oleh
          Direktorat Jenderal Anggaran (DJA), Kementerian Keuangan Republik
          Indonesia. Platform digunakan untuk menyimpan, mengklasifikasikan, dan
          mengelola dokumen terkait Penerimaan Negara Bukan Pajak (PNBP).
        </p>
        <p>
          Kebijakan Privasi ini berlaku bagi seluruh pengguna yang mengakses
          Platform. Dengan menggunakan Platform, Anda dianggap memahami dan
          menyetujui praktik pengelolaan data sebagaimana diuraikan di bawah
          ini.
        </p>
      </LegalSection>

      <LegalSection title="2. Data yang Kami Kumpulkan">
        <p>Platform dapat mengumpulkan dan memproses kategori data berikut:</p>
        <LegalList
          items={[
            <>
              <strong>Data akun pengguna</strong> — nama, alamat email, unit
              kerja, jabatan, serta peran (role) akses yang diberikan.
            </>,
            <>
              <strong>Dokumen dan metadata</strong> — berkas yang diunggah
              (PDF/Word) beserta metadata seperti judul, nomor dokumen, tanggal,
              klasifikasi jenis PNBP, taksonomi, tag, dan ringkasan.
            </>,
            <>
              <strong>Catatan aktivitas (audit log)</strong> — rekam jejak akses
              dan tindakan atas dokumen (lihat, buat, ubah, unggah, hapus),
              termasuk alamat IP dan informasi peramban (user agent) untuk
              keperluan keamanan dan akuntabilitas.
            </>,
            <>
              <strong>Data penggunaan</strong> — interaksi dengan fitur
              pencarian, statistik tampilan dokumen, dan preferensi tampilan.
            </>,
          ]}
        />
      </LegalSection>

      <LegalSection title="3. Tujuan Penggunaan Data">
        <p>Data digunakan secara terbatas untuk tujuan berikut:</p>
        <LegalList
          items={[
            "Menyediakan, mengoperasikan, dan memelihara fungsi Platform.",
            "Mengautentikasi pengguna dan menerapkan kontrol akses berbasis peran (RBAC).",
            "Mengelola, mengklasifikasikan, dan mempermudah penemuan kembali dokumen PNBP.",
            "Menjamin keamanan, integritas, dan akuntabilitas melalui pencatatan audit.",
            "Meningkatkan kualitas layanan dan akurasi klasifikasi dokumen.",
          ]}
        />
      </LegalSection>

      <LegalSection title="4. Pemrosesan oleh Kecerdasan Buatan (AI)">
        <p>
          Untuk mempercepat pengelolaan, dokumen yang diunggah dapat diproses
          secara otomatis oleh layanan kecerdasan buatan (AI) guna mengekstraksi
          metadata, menyusun ringkasan, serta menyarankan klasifikasi dan node
          taksonomi PNBP.
        </p>
        <LegalList
          items={[
            "Pemrosesan AI dilakukan untuk mendukung pengguna; hasil saran AI bersifat membantu dan tetap memerlukan verifikasi oleh pengguna yang berwenang.",
            "Isi dokumen yang diproses digunakan semata-mata untuk menghasilkan metadata dan klasifikasi pada Platform, bukan untuk tujuan di luar pengelolaan PNBP.",
            "Pengguna bertanggung jawab memastikan dokumen yang diunggah sesuai dengan ketentuan klasifikasi keamanan informasi yang berlaku.",
          ]}
        />
      </LegalSection>

      <LegalSection title="5. Pembagian dan Pengungkapan Data">
        <p>
          Platform merupakan sistem internal pemerintah. Kami tidak menjual atau
          memperdagangkan data Anda. Data hanya dapat diakses atau diungkapkan
          dalam hal:
        </p>
        <LegalList
          items={[
            "Akses oleh pengguna internal yang berwenang sesuai peran dan kebutuhan tugas (need-to-know).",
            "Pemenuhan kewajiban hukum, peraturan perundang-undangan, atau permintaan resmi dari instansi yang berwenang.",
            "Keperluan audit, pengawasan, dan penegakan tata kelola pemerintahan.",
          ]}
        />
      </LegalSection>

      <LegalSection title="6. Keamanan Data">
        <p>
          Kami menerapkan langkah-langkah keamanan teknis dan organisasi yang
          wajar untuk melindungi data, antara lain kontrol akses berbasis peran
          (RBAC), pencatatan audit, serta pengamanan transmisi data. Meskipun
          demikian, tidak ada sistem yang sepenuhnya bebas risiko; pengguna
          diharapkan menjaga kerahasiaan kredensial akun masing-masing.
        </p>
      </LegalSection>

      <LegalSection title="7. Penyimpanan dan Retensi Data">
        <p>
          Dokumen dan data terkait disimpan selama diperlukan untuk tujuan
          pengelolaan PNBP dan sesuai dengan ketentuan retensi arsip yang
          berlaku di lingkungan Kementerian Keuangan. Catatan audit dapat
          disimpan untuk jangka waktu yang ditetapkan guna keperluan keamanan
          dan akuntabilitas.
        </p>
      </LegalSection>

      <LegalSection title="8. Hak Pengguna">
        <p>
          Sesuai kewenangan dan ketentuan yang berlaku, pengguna dapat
          mengajukan permintaan untuk meninjau atau memperbaiki data akun, serta
          memperoleh penjelasan mengenai pengelolaan datanya melalui kontak yang
          tersedia. Permintaan akan diproses dengan tetap memperhatikan
          kewajiban hukum dan tata kelola arsip negara.
        </p>
      </LegalSection>

      <LegalSection title="9. Perubahan Kebijakan">
        <p>
          Kebijakan Privasi ini dapat diperbarui dari waktu ke waktu untuk
          menyesuaikan dengan perkembangan layanan atau ketentuan yang berlaku.
          Perubahan akan ditampilkan pada halaman ini dengan pembaruan tanggal
          "Terakhir diperbarui".
        </p>
      </LegalSection>

      <LegalSection title="10. Hubungi Kami">
        <p>
          Untuk pertanyaan terkait Kebijakan Privasi ini, silakan hubungi kami
          melalui surel{" "}
          <a
            href="mailto:dja@kemenkeu.go.id"
            className="font-medium text-blue-600 hover:underline"
          >
            dja@kemenkeu.go.id
          </a>{" "}
          atau Direktorat Jenderal Anggaran, Kementerian Keuangan Republik
          Indonesia, Jakarta.
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
